import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { HeadphonesSuggestedInfoPopup } from '../../components/Popups';
import GameStartTemplate from '../../components/Templates/Game/GameStartTemplate/GameStartTemplate';
import { ColorfulGameBG } from '../../constants/Games/gameBackground';
import { getGameTypeDetails } from '../../constants/Games/gameType';
import { AppToastInfoOption } from '../../constants/toast';
import {
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import useLocale from '../../hooks/useLocale';
import { I18n } from '../../models/Base';
import { ThemeType } from '../../models/GameControllerStore';
import {
  FingerWritingVariant,
  GameData,
  TextOrderingVariant,
} from '../../models/GameStore';
import { ProfileExample } from '../../models/UserStore';
import { getChallengeData } from '../../stores/GameControllerStore/mode-simple-challenge';
import { useGameStore, useGameStoreListField } from '../../stores/GameStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { arrayEqual } from '../../utils/array';

const { fetchSpace, fetchAlbum } = useSpaceStore.getState();
const { fetchGameCollectionsByIds, fetchGame } = useGameStore.getState();

const GamePreviewScreen: React.FC = () => {
  const {
    classroomId = '',
    gameSetId = '',
    gameId = '',
    pathChallengeIndex = '',
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSharedData } = useShareData();
  const query = new URLSearchParams(useLocation().search);
  const mode = query.get('mode');
  const l = useLocale();
  const { t } = useTranslation();
  const activeUserId = useSessionStore.getState().activeUserId;
  const [bgIndex, setbgIndex] = useState(Number);

  const isChallenge = !gameId ? true : false;

  useEffect(() => {
    if (activeUserId) {
      navigate(`/club/${classroomId}/journey/${gameSetId}`, { replace: true });
      navigate(location.pathname.replace('/share', '') + location.search);
    }
  }, [
    activeUserId,
    classroomId,
    gameSetId,
    location.pathname,
    location.search,
    navigate,
  ]);

  // MARK: Get Space
  const spaceListFieldName = `${classroomId}`;
  useSpaceStoreListField('spaces', spaceListFieldName);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceListFieldName, classroomId).catch(console.error);
    }
  }, [classroomId, spaceListFieldName]);

  const { classroom } = useSpaceStore((store) => {
    const space = store.getSpace(classroomId ?? '');
    const membership = store.getActiveUserMembership(classroomId ?? '');
    return {
      classroom: space,
      classroomMembership: membership,
    };
  }, shallow);

  // MARK: Get Album
  const albumListFieldName = `${classroomId}_${gameSetId}`;
  useSpaceStoreListField('albums', albumListFieldName);

  useEffect(() => {
    if (classroom && gameSetId) {
      fetchAlbum(
        {
          fieldName: albumListFieldName,
          classroomId: classroom.classroomId,
          gameSetId,
        },
        { headers: { 'cache-control': 'no-cache' } },
      ).catch(console.error);
    }
  }, [albumListFieldName, classroom, gameSetId]);

  const album = useSpaceStore(
    (store) => store.getAlbum(gameSetId ?? ''),
    shallow,
  );

  // MARK: Get GameCollection
  const gameCollectionFieldName = `album_${album?.gameSetId}`;
  useGameStoreListField('gameCollections', gameCollectionFieldName);

  useEffect(() => {
    if (album?.content?.gameCollectionIds && album?.gameSetId) {
      const gameCollectionIds = album?.content?.gameCollectionIds;
      if (gameCollectionIds) {
        fetchGameCollectionsByIds({
          fieldName: gameCollectionFieldName,
          gameCollectionIds: gameCollectionIds,
        }).catch(console.error);
      }
    }
  }, [
    album?.content?.gameCollectionIds,
    album?.gameSetId,
    gameCollectionFieldName,
  ]);

  const gameItems = useGameStore((store) => {
    const gameCollections = (album?.content?.gameCollectionIds ?? []).map(
      (id) => store.getGameCollection(id),
    );
    if (
      gameCollections.some((gameCollection) => gameCollection === undefined)
    ) {
      // only return data if all game collection loaded
      return undefined;
    }

    return gameCollections.flatMap(
      (gameCollection) =>
        gameCollection?.getPartItemArray((item) => item.game) ?? [],
    );
  }, arrayEqual(shallow));
  // MARK: Get Games And GameStatistics
  const gameCollectionIdsJoin = album?.content?.gameCollectionIds.join('_');
  const gamesFieldName = `${gameCollectionIdsJoin}`;
  const gameStatisticsFieldName = `${gameSetId}`;
  useGameStoreListField('games', gamesFieldName);
  useGameStoreListField('gameStatistics', gameStatisticsFieldName);
  const foundItem = gameItems?.find((item) => item.game?.gameId === gameId);
  useEffect(() => {
    if (gameItems && album) {
      const hash = foundItem?.game?.hash;
      const gameCollectionId = foundItem?.gameCollectionId;
      if (gameCollectionId && hash) {
        fetchGame({
          fieldName: gameCollectionIdsJoin ?? '',
          gameCollectionId,
          gameId,
          hash,
        }).catch(console.error);
      }
    }
  }, [
    album,
    foundItem?.game?.hash,
    foundItem?.gameCollectionId,
    gameCollectionIdsJoin,
    gameId,
    gameItems,
  ]);

  const gotGame = useGameStore((store) => {
    return store.getGame(gameId);
  });

  // const foundCollection = useGameStore((store) => store.getGameCollection)(
  //   foundItem?.gameCollectionId ?? '',
  // );
  // console.log(gotGame);

  const theme: ThemeType | undefined = gameId ? gotGame?.gameType : 'challenge';
  useEffect(() => {
    setbgIndex(Math.floor(ColorfulGameBG.length * Math.random()));
  }, []);

  const iconTag = foundItem?.icon?.tag;
  // const gameTitle = l(
  //   foundCollection?.parts[foundItem?.partId ?? '00'].i18nPartInfo,
  // )?.name;

  const gameTitle = gotGame?.i18nGameData
    ? l(
        gotGame.i18nGameData as I18n<
          GameData<TextOrderingVariant | FingerWritingVariant>
        >,
      ).title
    : '';

  const { showPopup, hidePopup } = usePopup();
  const onClickHeadphoneSuggested = useCallback(() => {
    showPopup(<HeadphonesSuggestedInfoPopup onPrimary={hidePopup} />);
  }, [hidePopup, showPopup]);

  // const onClickShare = useCallback(() => {
  //   const pageUrl = window.location.href;

  //   const updatedUrl = new URL(pageUrl);

  //   if (navigator.share) {
  //     navigator.share({ url: updatedUrl.toString() }).catch((err) => {
  //       console.error('Failed to share the URL: ', err);
  //     });
  //   } else {
  //     navigator.clipboard
  //       .writeText(updatedUrl.toString())
  //       .then(() => {
  //         toast(t('Link is copied!'), {
  //           ...AppToastInfoOption,
  //           id: 'clipboard',
  //         });
  //       })
  //       .catch((err) => {
  //         console.error('Failed to copy the URL: ', err);
  //       });
  //   }
  // }, [t]);

  const onSharePath = useCallback(() => {
    const pageUrl = window.location.href;

    const updatedUrl = new URL(pageUrl);

    if (navigator.share) {
      navigator
        .share({ url: decodeURIComponent(updatedUrl.toString()) })
        .catch((err) => {
          console.error('Failed to share the URL: ', err);
        });
    } else {
      navigator.clipboard
        .writeText(decodeURIComponent(updatedUrl.toString()))
        .then(() => {
          toast(t('Link is copied!'), {
            ...AppToastInfoOption,
            id: 'clipboard',
          });
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  }, [t]);

  const { title, icon } = getChallengeData(
    pathChallengeIndex === 'super' || pathChallengeIndex === 'comprehensive'
      ? pathChallengeIndex
      : parseInt(pathChallengeIndex),
  );

  // console.log(window.location.href.replace('/share', ''));

  return (
    <GameStartTemplate
      profile={undefined}
      currencies={[]}
      isAssistedGame={mode === 'tutorial'}
      gameFee={foundItem?.unlockFee.token}
      gameBGIndex={bgIndex}
      // challengeGameTotal={isChallenge ? 3 : undefined}
      // challengeTitle={isChallenge ? title : undefined}
      gameStartInfoBoardProps={{
        // clubIcon: undefined,
        // clubName: 'Guest',
        // profile: ProfileExample,
        gameIcon: isChallenge
          ? icon
          : iconTag
          ? getGameTypeDetails(iconTag ?? '').icon
          : undefined,
        gameTitle: isChallenge ? title : gameTitle ?? '',
        theme: theme,
        gameModeName: !gameId
          ? undefined
          : mode === 'tutorial'
          ? 'Interactive Learning'
          : 'Memory Practice',
        typeName: iconTag ? getGameTypeDetails(iconTag).displayName : undefined,
        latestScore: undefined,
        latestTime: undefined,
        playCount: undefined,
        avgScore: undefined,
        style: undefined,
      }}
      gameStartVSBoardProps={{
        profile: ProfileExample,
        opponentRecords: [ProfileExample],
        withAIBadge: true,
        reward: undefined,
      }}
      memoryHelperProps={{
        questionPanel: undefined,
        isUseTranslate: false,
        originalLanguage: '',
        translatedLanguage: '',
        originalAudioTts: undefined,
        translatedAudioTts: undefined,
        translationResult: undefined,
        isGameStart: true,
        onSwitchTranslate: () => {},
        onClickLanguage: () => {},
      }}
      onClickStart={() => {
        if (classroom && album) {
          setSharedData({
            pathName: location.pathname.replace('/share', '') + location.search,
          });

          navigate(getWebPageLink(WebPageType.JOYAJOY, WebPageLink.Entry));
        } else {
          toast('Data is not ready yet');
        }
      }}
      isLoading={false}
      onClickExit={() => {
        navigate(-1);
      }}
      onClickHeadphonesSuggested={onClickHeadphoneSuggested}
      onClickShare={onSharePath}
      onClickMemoryHelper={() => {
        if (classroom && album) {
          setSharedData({
            pathName: location.pathname.replace('/share', '') + location.search,
          });

          navigate(getWebPageLink(WebPageType.JOYAJOY, WebPageLink.Entry));
        } else {
          toast('Data is not ready yet');
        }
      }}
    />
  );
};

export default GamePreviewScreen;
